"use client"

import * as React from "react"
import { UserCheck, Plus, Trash2, Pencil, Shield, X, CheckSquare, Square, Info, Loader2, AlertTriangle, FolderOpen, LogOut } from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account, UserProfile } from "@/types/workspace"
import { MemberWorkspaceCell } from "@/components/settings/tabs/MemberWorkspaceCell"
import { toast } from "@/lib/toast"

export interface UsersSettingsTabProps {
  usersList: UserProfile[]
  user?: UserProfile | null
  profileRole?: string
  currentAccount: Account | null
  activeAccountId: string
  installedApps: any[]
  handleInviteUser: (email: string, name: string, role: string, workspaces?: Array<{ workspaceId: string; role: string }>) => Promise<any> | void
  handleUpdateAccountMember?: (userId: string, data: { name?: string; role?: string; status?: string; customRoleId?: string | null }) => Promise<any> | void
  handleUpdateUserRole?: (userId: string, role: string) => void
  handleRemoveUser: (userId: string) => Promise<any> | void
  t: Record<string, string>
}

export function UsersSettingsTab({
  usersList,
  user,
  profileRole,
  currentAccount,
  activeAccountId,
  installedApps,
  handleInviteUser,
  handleUpdateAccountMember,
  handleUpdateUserRole,
  handleRemoveUser,
  t,
}: UsersSettingsTabProps) {
  const isAdmin = profileRole === "admin" || profileRole === "owner"
  const [memberFilter, setMemberFilter] = React.useState<"all" | "active" | "inactive" | "pending">("all")
  const [pendingInvites, setPendingInvites] = React.useState<any[]>([])

  // Invite Modal State
  const [showInviteModal, setShowInviteModal] = React.useState(false)
  const [inviteEmail, setInviteEmail] = React.useState("")
  const [inviteRole, setInviteRole] = React.useState("member")
  const [inviteWorkspaces, setInviteWorkspaces] = React.useState<Record<string, { enabled: boolean; role: string }>>({})
  const [isInviting, setIsInviting] = React.useState(false)

  // Initialize workspace selection whenever modal is opened or workspaces change
  React.useEffect(() => {
    if (showInviteModal && currentAccount?.workspaces) {
      const initial: Record<string, { enabled: boolean; role: string }> = {}
      currentAccount.workspaces.forEach((ws) => {
        initial[ws.id] = { enabled: true, role: "member" }
      })
      setInviteWorkspaces(initial)
    }
  }, [showInviteModal, currentAccount])

  // Edit Member Modal State
  const [editingMember, setEditingMember] = React.useState<any>(null)
  const [editMemberName, setEditMemberName] = React.useState("")
  const [editMemberRole, setEditMemberRole] = React.useState("member")
  const [editMemberStatus, setEditMemberStatus] = React.useState<"active" | "inactive">("active")
  const [editMemberProfile, setEditMemberProfile] = React.useState("")
  const [isSavingMember, setIsSavingMember] = React.useState(false)

  // Delete Member Confirmation Modal State
  const [memberToDelete, setMemberToDelete] = React.useState<any | null>(null)
  const [isDeletingMember, setIsDeletingMember] = React.useState(false)

  // Leave Company Modal State
  const [memberToLeave, setMemberToLeave] = React.useState<any | null>(null)
  const [isLeavingMember, setIsLeavingMember] = React.useState(false)

  // Custom Roles State
  const [selectedGroupedProfileName, setSelectedGroupedProfileName] = React.useState<string | null>(null)
  const [profileEditingName, setProfileEditingName] = React.useState("")
  const [profileAppPerms, setProfileAppPerms] = React.useState<Record<string, { access: boolean; c: boolean; r: boolean; u: boolean; d: boolean }>>({})
  const [isCreatingNewProfile, setIsCreatingNewProfile] = React.useState(false)
  const [allCustomRoles, setAllCustomRoles] = React.useState<any[]>([])

  const groupedProfiles = React.useMemo(() => {
    const groups: Record<string, any[]> = {}
    allCustomRoles.forEach((role) => {
      if (!groups[role.name]) {
        groups[role.name] = []
      }
      groups[role.name].push(role)
    })
    return groups
  }, [allCustomRoles])

  React.useEffect(() => {
    if (selectedGroupedProfileName && !isCreatingNewProfile) {
      const perms: Record<string, { access: boolean; c: boolean; r: boolean; u: boolean; d: boolean }> = {}
      installedApps.forEach((ia) => {
        const role = allCustomRoles.find((r) => r.name === selectedGroupedProfileName && r.appId === ia.app.id)
        if (role) {
          perms[ia.app.id] = {
            access: true,
            c: role.scopes?.includes("c") || false,
            r: role.scopes?.includes("r") || false,
            u: role.scopes?.includes("u") || false,
            d: role.scopes?.includes("d") || false,
          }
        } else {
          perms[ia.app.id] = { access: false, c: false, r: false, u: false, d: false }
        }
      })
      setProfileAppPerms(perms)
      setProfileEditingName(selectedGroupedProfileName)
    }
  }, [selectedGroupedProfileName, allCustomRoles, installedApps, isCreatingNewProfile])

  // Fetch custom roles on mount
  React.useEffect(() => {
    const fetchRoles = async () => {
      const token = document.cookie.split("; ").find((r) => r.startsWith("token="))?.split("=")[1]
      if (!token) return
      try {
        const res = await fetch("/api/graphql", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          body: JSON.stringify({
            query: `
              query GetAccountCustomRoles {
                accountCustomRoles {
                  id
                  accountId
                  appId
                  name
                  scopes
                }
              }
            `,
          }),
        })
        const result = await res.json()
        if (result.data && result.data.accountCustomRoles) {
          setAllCustomRoles(result.data.accountCustomRoles)
        }
      } catch (e) {
        console.error(e)
      }
    }
    fetchRoles()
  }, [])

  // User Workspaces
  const [userWorkspacesMap, setUserWorkspacesMap] = React.useState<Record<string, string[]>>({})

  const fetchUserWorkspaces = React.useCallback(async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          query: `
            query GetUserWorkspaceMemberships($userId: ID!) {
              userWorkspaceMemberships(userId: $userId) {
                workspaceId
              }
            }
          `,
          variables: { userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.userWorkspaceMemberships) {
        setUserWorkspacesMap(prev => ({
          ...prev,
          [userId]: result.data.userWorkspaceMemberships.map((m: any) => m.workspaceId)
        }))
      }
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleToggleUserWorkspace = React.useCallback(async (userId: string, workspaceId: string, isAssigned: boolean) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    const currentList = userWorkspacesMap[userId] || []
    const updatedList = isAssigned
      ? currentList.filter(id => id !== workspaceId)
      : [...currentList, workspaceId]

    setUserWorkspacesMap(prev => ({
      ...prev,
      [userId]: updatedList
    }))

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          query: `
            mutation SetUserWorkspaces($userId: ID!, $workspaceIds: [ID!]!) {
              setUserWorkspaces(userId: $userId, workspaceIds: $workspaceIds)
            }
          `,
          variables: { userId, workspaceIds: updatedList }
        })
      })
      const result = await res.json()
      if (result.errors) {
        toast.error("Erro ao atualizar workspaces do usuário")
      } else {
        toast.success("Workspaces do usuário atualizados!")
      }
    } catch (e) {
      console.error(e)
      toast.error("Erro de conexão ao atualizar workspaces")
    }
  }, [userWorkspacesMap])

  // Effective users list (with fallback to current user if empty)
  const effectiveUsersList = React.useMemo(() => {
    if (usersList && usersList.length > 0) return usersList
    if (user) {
      return [
        {
          id: user.id,
          userId: user.id,
          name: user.name || "Usuário Principal",
          email: user.email || "",
          avatarUrl: user.avatarUrl || null,
          role: profileRole || "owner",
          status: "active",
        }
      ]
    }
    return []
  }, [usersList, user, profileRole])

  // Filtered members list
  const filteredUsersList = React.useMemo(() => {
    if (memberFilter === "all") return effectiveUsersList
    if (memberFilter === "active") return effectiveUsersList.filter((u: any) => (u.status || "active") !== "inactive")
    if (memberFilter === "inactive") return effectiveUsersList.filter((u: any) => u.status === "inactive")
    return []
  }, [effectiveUsersList, memberFilter])

  const selectedInviteWsCount = React.useMemo(() => {
    return Object.values(inviteWorkspaces).filter((w) => w.enabled).length
  }, [inviteWorkspaces])

  return (
    <>
      <TabsContent value="users" className="outline-none space-y-6">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <UserCheck className="size-5 text-[#004EB1]" />
            {t.settingsTabUsers || "Usuários & Perfis"}
          </h3>
          <p className="text-xs text-zinc-550 dark:text-zinc-400 mt-1 font-sans">
            {t.usersSubtitle || "Gerencie acessos, membros e permissões dos usuários da conta."}
          </p>
        </div>

        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid grid-cols-2 w-72 mb-4 bg-zinc-100 dark:bg-zinc-800 p-0.5 rounded-lg h-auto">
            <TabsTrigger value="members" className="text-xs py-1.5 font-semibold rounded-md cursor-pointer focus:outline-none">
              {t.usersTabMembers || "Membros"}
            </TabsTrigger>
            <TabsTrigger value="custom-roles" className="text-xs py-1.5 font-semibold rounded-md cursor-pointer focus:outline-none">
              {t.usersTabCustomRoles || "Perfis de Acesso"}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="space-y-4">
            {/* Top Bar: Ações & Filtros de Membros */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl">
              {/* Botões de Filtros */}
              <div className="flex items-center gap-1 bg-zinc-200/60 dark:bg-zinc-900 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setMemberFilter("all")}
                  className={`text-xs font-semibold px-3 py-1 rounded-md transition-all cursor-pointer ${memberFilter === "all"
                    ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-xs font-bold"
                    : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setMemberFilter("active")}
                  className={`text-xs font-semibold px-3 py-1 rounded-md transition-all cursor-pointer ${memberFilter === "active"
                    ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-xs font-bold"
                    : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                >
                  Ativos
                </button>
                <button
                  type="button"
                  onClick={() => setMemberFilter("inactive")}
                  className={`text-xs font-semibold px-3 py-1 rounded-md transition-all cursor-pointer ${memberFilter === "inactive"
                    ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-xs font-bold"
                    : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                >
                  Desativados
                </button>
                <button
                  type="button"
                  onClick={() => setMemberFilter("pending")}
                  className={`text-xs font-semibold px-3 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${memberFilter === "pending"
                    ? "bg-white dark:bg-zinc-800 text-amber-600 dark:text-amber-400 shadow-xs font-bold"
                    : "text-zinc-500 hover:text-amber-600 dark:hover:text-amber-400"
                    }`}
                >
                  Pendentes
                  <span className="bg-amber-100 dark:bg-amber-955/40 text-amber-700 dark:text-amber-400 text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                    {pendingInvites.length}
                  </span>
                </button>
              </div>

              <Button
                type="button"
                disabled={!isAdmin}
                onClick={() => {
                  if (!isAdmin) {
                    toast.error("Apenas administradores ou o proprietário da conta podem convidar membros.")
                    return
                  }
                  setShowInviteModal(true)
                }}
                className={`text-xs font-semibold h-9 px-4 flex items-center gap-1.5 font-sans shadow-sm ${isAdmin
                  ? "bg-[#004EB1] hover:bg-[#003d8d] text-white cursor-pointer"
                  : "bg-zinc-200 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed opacity-70"
                  }`}
                title={!isAdmin ? "Apenas administradores da conta podem convidar novos membros" : undefined}
              >
                <Plus className="size-4" />
                Convidar Membro
              </Button>
            </div>

            {/* Lista de Convites Pendentes */}
            {memberFilter === "pending" && (
              pendingInvites.length > 0 ? (
                <Card className="border-amber-200 dark:border-amber-900/40 bg-amber-50/20 dark:bg-amber-955/10">
                  <CardHeader className="py-3 px-4 border-b border-amber-100 dark:border-amber-900/20">
                    <CardTitle className="text-xs font-bold text-amber-800 dark:text-amber-400 font-sans flex items-center gap-2">
                      <UserCheck className="size-4 text-amber-600" />
                      Convites Pendentes (Expiração em 72h)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 divide-y divide-amber-100 dark:divide-amber-900/20">
                    {pendingInvites.map((inv) => {
                      const hoursLeft = Math.max(0, Math.round((new Date(inv.expiresAt).getTime() - Date.now()) / (3600 * 1000)))
                      return (
                        <div key={inv.id} className="p-3.5 flex items-center justify-between gap-4 font-sans text-xs">
                          <div className="flex items-center gap-3">
                            <div className="size-7 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center font-bold text-xs">
                              ✉️
                            </div>
                            <div>
                              <div className="font-semibold text-zinc-900 dark:text-white">{inv.email}</div>
                              <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                <span>Expira em ~{hoursLeft} horas (Limite de 72h)</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                              Aguardando Aceite
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => toast.success(`Convite reenviado para ${inv.email}`)}
                              className="h-7 text-xs font-semibold px-2 text-[#004EB1] hover:bg-blue-50 dark:hover:bg-blue-955/20 cursor-pointer"
                            >
                              Reenviar
                            </Button>
                            <button
                              type="button"
                              onClick={() => setPendingInvites(prev => prev.filter(i => i.id !== inv.id))}
                              className="text-red-500 hover:text-red-600 p-1 rounded-lg cursor-pointer"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                  <CardContent className="py-12 text-center text-xs text-zinc-400 dark:text-zinc-500 italic font-sans">
                    Nenhum convite pendente no momento.
                  </CardContent>
                </Card>
              )
            )}

            {/* Lista Principal de Membros */}
            {memberFilter !== "pending" && (
              <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                <CardContent className="p-0 divide-y divide-zinc-150 dark:divide-zinc-800">
                  {filteredUsersList.length === 0 ? (
                    <div className="py-12 text-center text-xs text-zinc-400 dark:text-zinc-500 italic font-sans">
                      {memberFilter === "active"
                        ? "Nenhum membro ativo encontrado."
                        : memberFilter === "inactive"
                          ? "Nenhum membro desativado encontrado."
                          : "Nenhum membro cadastrado nesta conta."}
                    </div>
                  ) : (
                    filteredUsersList.map((usr: any) => {
                      const mRole = usr.role || "member"
                      const mStatus = usr.status || "active"
                      const isOwner = mRole === "owner"
                      const isUserAdmin = mRole === "admin"
                      const isInactive = mStatus === "inactive"
                      const isCurrentUserOwner = profileRole === "owner"
                      const canEditThisMember = isOwner ? isCurrentUserOwner : isAdmin

                      return (
                        <div key={usr.id} className={`p-4 flex items-center justify-between gap-4 transition-colors ${isInactive ? "bg-zinc-50/60 dark:bg-zinc-950/30 opacity-75" : ""}`}>
                          <div className="flex items-center gap-3">
                            {usr.avatarUrl ? (
                              <img src={usr.avatarUrl} alt={usr.name || usr.email} className="size-8 rounded-full object-cover" />
                            ) : (
                              <div className="size-8 rounded-full bg-gradient-to-tr from-[#004EB1] to-blue-400 text-white flex items-center justify-center font-bold text-xs shadow-xs">
                                {(usr.name || usr.email || "?")[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-semibold text-xs text-zinc-900 dark:text-white flex items-center gap-1.5">
                                {usr.name || "Usuário"}
                                {user?.id === (usr.userId || usr.id) && (
                                  <span className="text-[10px] bg-blue-100 dark:bg-blue-955 text-[#004EB1] dark:text-blue-300 font-bold px-1.5 py-0.2 rounded-full">
                                    Você
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-zinc-500">{usr.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold capitalize ${isOwner
                              ? "bg-purple-100 text-purple-750 dark:bg-purple-955/40 dark:text-purple-400"
                              : isUserAdmin
                                ? "bg-blue-100 text-blue-750 dark:bg-blue-955/40 dark:text-blue-400"
                                : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                              }`}>
                              {isOwner ? "Proprietário" : isUserAdmin ? "Administrador" : "Membro"}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isInactive
                              ? "bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                              }`}>
                              {isInactive ? "Desativado" : "Ativo"}
                            </span>
                            <MemberWorkspaceCell
                              member={{ userId: usr.userId || usr.id, role: mRole }}
                              allWorkspaces={currentAccount?.workspaces || []}
                              userWorkspacesMap={userWorkspacesMap}
                              fetchUserWorkspaces={fetchUserWorkspaces}
                            />
                            {canEditThisMember && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingMember(usr)
                                  setEditMemberName(usr.name || "")
                                  setEditMemberRole(mRole)
                                  setEditMemberStatus(mStatus as "active" | "inactive")
                                  setEditMemberProfile(usr.userCustomRole?.name || "")
                                }}
                                className="h-7 text-xs font-semibold px-2.5 text-[#004EB1] hover:bg-blue-50 dark:hover:bg-blue-955/20 cursor-pointer"
                              >
                                <Pencil className="size-3.5" />
                              </Button>
                            )}
                            {user?.id === (usr.userId || usr.id) && !isOwner && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setMemberToLeave(usr)}
                                className="h-7 text-xs font-semibold px-2.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-955/20 hover:text-amber-700 cursor-pointer flex items-center gap-1.5"
                                title="Sair desta empresa"
                              >
                                <LogOut className="size-3.5" />
                              </Button>
                            )}
                            {isAdmin && !isOwner && user?.id !== (usr.userId || usr.id) && (
                              <button
                                type="button"
                                onClick={() => setMemberToDelete(usr)}
                                className="text-red-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-955/20 cursor-pointer transition-colors"
                                title="Excluir membro da empresa"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Sub-Aba: Perfis de Acesso Customizados */}
          <TabsContent value="custom-roles" className="space-y-4">
            <div className="flex gap-6 flex-col md:flex-row items-stretch">
              {/* Coluna Esquerda: Lista de Perfis Globais */}
              <Card className="w-full md:w-80 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0 flex flex-col justify-between">
                <CardHeader className="pb-3 border-b border-zinc-150 dark:border-zinc-800/80">
                  <CardTitle className="text-sm font-bold text-zinc-850 dark:text-zinc-100 font-sans">
                    Perfis Globais
                  </CardTitle>
                  <CardDescription className="text-xs font-sans">
                    Selecione ou crie papéis de acesso unificados na sua conta.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 flex-1 overflow-y-auto max-h-[500px] space-y-2">
                  {Object.keys(groupedProfiles).length === 0 ? (
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 italic p-3 text-center">Nenhum perfil de acesso criado.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {Object.keys(groupedProfiles).map((name) => {
                        const isActive = selectedGroupedProfileName === name && !isCreatingNewProfile;
                        return (
                          <button
                            key={name}
                            type="button"
                            onClick={() => {
                              setSelectedGroupedProfileName(name);
                              setIsCreatingNewProfile(false);
                            }}
                            className={`w-full text-left p-3 rounded-xl text-xs font-semibold flex items-center justify-between transition-all cursor-pointer border ${isActive
                              ? "bg-[#004EB1]/5 border-[#004EB1]/20 text-[#004EB1] dark:bg-blue-955/20 dark:border-blue-900/30 dark:text-blue-400 font-bold"
                              : "bg-zinc-50/50 dark:bg-zinc-950/20 border-zinc-150 dark:border-zinc-800/60 text-zinc-650 hover:bg-zinc-100/50 dark:hover:bg-zinc-850 hover:text-zinc-900 dark:hover:text-zinc-200"
                              }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Shield className={`size-4 ${isActive ? "text-[#004EB1] dark:text-blue-400" : "text-zinc-400"}`} />
                              <span className="truncate">{name}</span>
                            </div>
                            <span className="text-[9px] bg-zinc-100 dark:bg-zinc-850 px-2.5 py-0.5 rounded-full text-zinc-500 dark:text-zinc-400 font-bold shrink-0">
                              {groupedProfiles[name].length} {groupedProfiles[name].length === 1 ? "app" : "apps"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
                <div className="p-3 border-t border-zinc-150 dark:border-zinc-800/80 bg-zinc-50/50 dark:bg-zinc-950/20">
                  <Button
                    type="button"
                    onClick={() => {
                      setSelectedGroupedProfileName(null);
                      setIsCreatingNewProfile(true);
                      setProfileEditingName("");
                      const perms: Record<string, { access: boolean, c: boolean, r: boolean, u: boolean, d: boolean }> = {};
                      installedApps.forEach((ia) => {
                        perms[ia.app.id] = { access: false, c: false, r: false, u: false, d: false };
                      });
                      setProfileAppPerms(perms);
                    }}
                    className="w-full bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold py-2 px-4 rounded-xl flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Plus className="size-4" />
                    Novo Perfil
                  </Button>
                </div>
              </Card>

              {/* Coluna Direita: Formulário de Configuração (Matriz CRUD) */}
              <Card className="flex-1 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex flex-col justify-between">
                <CardContent className="p-6 space-y-6">
                  {selectedGroupedProfileName || isCreatingNewProfile ? (
                    <div className="space-y-6">
                      {/* Campo Nome do Perfil */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 uppercase tracking-wider font-sans">
                          Nome do Perfil de Acesso
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Gestor Financeiro, Vendedor, Operador"
                          value={profileEditingName}
                          onChange={(e) => setProfileEditingName(e.target.value)}
                          className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:bg-zinc-950 dark:text-white font-sans bg-white focus:ring-1 focus:ring-[#004EB1]"
                        />
                      </div>

                      {/* Matriz CRUD por App */}
                      <div className="space-y-2">
                        <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 uppercase tracking-wider font-sans block">
                          Matriz de Permissões por Aplicativo
                        </label>
                        <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden bg-zinc-50/30 dark:bg-zinc-950/30 max-h-[300px] overflow-y-auto">
                          <table className="w-full text-left border-collapse text-xs font-sans">
                            <thead>
                              <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 text-zinc-500 font-bold select-none">
                                <th className="p-3">Aplicativo</th>
                                <th className="p-3 text-center">Acesso</th>
                                <th className="p-3 text-center">Criar (C)</th>
                                <th className="p-3 text-center">Ler (R)</th>
                                <th className="p-3 text-center">Atualizar (U)</th>
                                <th className="p-3 text-center">Deletar (D)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {installedApps.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="p-6 text-center text-zinc-450 italic">Nenhum aplicativo instalado no workspace corporativo.</td>
                                </tr>
                              ) : (
                                installedApps.filter(ia => ia.status === "active").map((ia) => {
                                  const app = ia.app;
                                  const perm = profileAppPerms[app.id] || { access: false, c: false, r: false, u: false, d: false };
                                  return (
                                    <tr key={app.id} className="border-b border-zinc-150 dark:border-zinc-800/80 text-zinc-850 dark:text-zinc-200 hover:bg-zinc-100/30 dark:hover:bg-zinc-850/10">
                                      <td className="p-3 font-semibold flex items-center gap-2.5 min-w-0">
                                        <span className="truncate">{app.name}</span>
                                      </td>
                                      <td className="p-3 text-center">
                                        <input
                                          type="checkbox"
                                          checked={perm.access}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setProfileAppPerms(prev => ({
                                              ...prev,
                                              [app.id]: {
                                                ...prev[app.id],
                                                access: checked,
                                                c: checked ? (prev[app.id]?.c ?? false) : false,
                                                r: checked ? (prev[app.id]?.r ?? false) : false,
                                                u: checked ? (prev[app.id]?.u ?? false) : false,
                                                d: checked ? (prev[app.id]?.d ?? false) : false,
                                              }
                                            }));
                                          }}
                                          className="rounded border-zinc-300 dark:border-zinc-700 text-[#004EB1] focus:ring-[#004EB1] size-4 cursor-pointer"
                                        />
                                      </td>
                                      {["c", "r", "u", "d"].map((scope) => (
                                        <td key={scope} className="p-3 text-center">
                                          <input
                                            type="checkbox"
                                            disabled={!perm.access}
                                            checked={(perm as any)[scope] || false}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setProfileAppPerms(prev => ({
                                                ...prev,
                                                [app.id]: {
                                                  ...prev[app.id],
                                                  [scope]: checked
                                                }
                                              }));
                                            }}
                                            className="rounded border-zinc-300 dark:border-zinc-700 text-[#004EB1] focus:ring-[#004EB1] size-4 cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Ações de Salvar e Excluir */}
                      <div className="flex justify-end gap-2.5 pt-4 border-t border-zinc-150 dark:border-zinc-800/80">
                        {selectedGroupedProfileName && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={async () => {
                              const token = document.cookie.split("; ").find(r => r.startsWith("token="))?.split("=")[1];
                              if (!token) return;
                              const rolesToDelete = (allCustomRoles || []).filter(r => r.name === selectedGroupedProfileName);
                              for (const r of rolesToDelete) {
                                await fetch("/api/graphql", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                                  body: JSON.stringify({ query: `mutation DeleteRole($id: ID!) { deleteCustomRole(id: $id) }`, variables: { id: r.id } })
                                });
                              }
                              toast.success("Perfil de acesso excluído com sucesso!");
                              setSelectedGroupedProfileName(null);
                              setIsCreatingNewProfile(false);
                            }}
                            className="border-red-200 hover:bg-red-50 hover:text-red-600 text-red-500 text-xs font-semibold h-9 px-4 flex items-center gap-1 cursor-pointer font-sans"
                          >
                            <Trash2 className="size-3.5" />
                            Excluir Perfil
                          </Button>
                        )}
                        <Button
                          type="button"
                          disabled={!profileEditingName.trim()}
                          onClick={async () => {
                            const token = document.cookie.split("; ").find(r => r.startsWith("token="))?.split("=")[1];
                            if (!token) return;
                            const oldRoles = selectedGroupedProfileName ? (allCustomRoles || []).filter(r => r.name === selectedGroupedProfileName) : [];
                            for (const ia of installedApps) {
                              const app = ia.app;
                              const perm = profileAppPerms[app.id] || { access: false, c: false, r: false, u: false, d: false };
                              const existingRole = oldRoles.find(r => r.appId === app.id);
                              const scopes: string[] = [];
                              if (perm.c) scopes.push("c");
                              if (perm.r) scopes.push("r");
                              if (perm.u) scopes.push("u");
                              if (perm.d) scopes.push("d");
                              if (perm.access) {
                                if (existingRole) {
                                  await fetch("/api/graphql", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                                    body: JSON.stringify({
                                      query: `mutation UpdateRole($id: ID!, $name: String!, $scopes: [String!]!) { updateCustomRole(id: $id, name: $name, scopes: $scopes) { id } }`,
                                      variables: { id: existingRole.id, name: profileEditingName, scopes }
                                    })
                                  });
                                } else if (activeAccountId) {
                                  await fetch("/api/graphql", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                                    body: JSON.stringify({
                                      query: `mutation CreateRole($accountId: ID!, $appId: ID!, $name: String!, $scopes: [String!]!) { createCustomRole(accountId: $accountId, appId: $appId, name: $name, scopes: $scopes) { id } }`,
                                      variables: { accountId: activeAccountId, appId: app.id, name: profileEditingName, scopes }
                                    })
                                  });
                                }
                              } else if (existingRole) {
                                await fetch("/api/graphql", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                                  body: JSON.stringify({ query: `mutation DeleteRole($id: ID!) { deleteCustomRole(id: $id) }`, variables: { id: existingRole.id } })
                                });
                              }
                            }
                            const fetchRoles = async () => {
                              const res = await fetch("/api/graphql", {
                                method: "POST",
                                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                                body: JSON.stringify({
                                  query: `query GetAccountCustomRoles { accountCustomRoles { id accountId appId name scopes } }`
                                })
                              });
                              const result = await res.json();
                              if (result.data?.accountCustomRoles) {
                                setAllCustomRoles(result.data.accountCustomRoles);
                              }
                            };
                            await fetchRoles();
                            toast.success("Perfil de acesso salvo com sucesso!");
                            setSelectedGroupedProfileName(profileEditingName);
                            setIsCreatingNewProfile(false);
                          }}
                          className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 cursor-pointer font-sans"
                        >
                          Salvar Perfil
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center h-full">
                      <span className="text-4xl">🛡️</span>
                      <h4 className="text-sm font-bold text-zinc-750 dark:text-zinc-200 font-sans mt-3">Gerenciamento de Perfis Globais</h4>
                      <p className="text-xs text-zinc-450 dark:text-zinc-500 max-w-sm mt-1.5 font-sans leading-relaxed">
                        Crie papéis unificados (como &quot;Vendas&quot;, &quot;T.I.&quot;) e defina de uma só vez as permissões de acesso e CRUD para múltiplos aplicativos.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </TabsContent>

      {/* Modal Convidar Membro */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between shrink-0">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <UserCheck className="size-4 text-[#004EB1]" />
                Convidar Novo Membro
              </h3>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!inviteEmail) return
                const nameToSend = inviteEmail.split("@")[0].trim()

                const selectedWs =
                  inviteRole === "admin"
                    ? []
                    : (currentAccount?.workspaces || [])
                      .filter((ws) => inviteWorkspaces[ws.id]?.enabled)
                      .map((ws) => ({
                        workspaceId: ws.id,
                        role: inviteWorkspaces[ws.id]?.role || "member",
                      }))

                if (inviteRole !== "admin" && selectedWs.length === 0) {
                  toast.error("Selecione pelo menos um workspace para o novo membro.")
                  return
                }

                setIsInviting(true)
                try {
                  await handleInviteUser(inviteEmail, nameToSend, inviteRole, selectedWs)
                  toast.success(`Convite enviado por e-mail para ${inviteEmail}!`)
                  setPendingInvites((prev) => [
                    ...prev,
                    {
                      id: `inv-${Date.now()}`,
                      email: inviteEmail,
                      name: nameToSend,
                      role: inviteRole,
                      invitedAt: new Date().toISOString(),
                      expiresAt: new Date(Date.now() + 72 * 3600 * 1000).toISOString(),
                    },
                  ])
                  setInviteEmail("")
                  setShowInviteModal(false)
                } catch (err: any) {
                  toast.error(err?.message || "Erro ao enviar convite.")
                } finally {
                  setIsInviting(false)
                }
              }}
              className="p-5 space-y-4 overflow-y-auto flex-1"
            >
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">E-mail</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="carlos@suaempresa.com.br"
                  className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:border-[#004EB1]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Função</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer"
                >
                  <option value="member">Membro</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {/* Seção de Workspaces */}
              {inviteRole === "admin" ? (
                <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-955/20 border border-blue-200 dark:border-blue-900/40 space-y-1 text-xs">
                  <div className="flex items-center gap-2 font-bold text-[#004EB1] dark:text-blue-300">
                    <Shield className="size-4" />
                    Acesso Universal de Administrador
                  </div>
                  <p className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    Como Administrador da Conta, este usuário terá acesso total e privilégios de administrador em <strong>todos os workspaces</strong> da organização automaticamente.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 pt-1 border-t border-zinc-150 dark:border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                        Workspaces & Permissões
                      </label>
                      <p className="text-[10px] text-zinc-500">
                        Selecione pelo menos 1 workspace que este membro poderá acessar:
                      </p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedInviteWsCount > 0
                      ? "bg-blue-50 text-[#004EB1] dark:bg-blue-955/40 dark:text-blue-300"
                      : "bg-red-100 text-red-700 dark:bg-red-955/40 dark:text-red-400"
                      }`}>
                      {selectedInviteWsCount} selecionado(s)
                    </span>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1 border border-zinc-200 dark:border-zinc-800 rounded-lg p-2 bg-zinc-50/50 dark:bg-zinc-950/40 scrollbar-thin">
                    {(currentAccount?.workspaces || []).length === 0 ? (
                      <div className="p-3 text-center text-xs text-zinc-400">Nenhum workspace encontrado nesta conta.</div>
                    ) : (
                      (currentAccount?.workspaces || []).map((ws) => {
                        const isSelected = inviteWorkspaces[ws.id]?.enabled ?? true
                        const currentWsRole = inviteWorkspaces[ws.id]?.role || "member"

                        return (
                          <div
                            key={ws.id}
                            className={`p-2 rounded-md border transition-all flex items-center justify-between gap-3 ${isSelected
                              ? "bg-white dark:bg-zinc-900 border-zinc-250 dark:border-zinc-700 shadow-2xs"
                              : "bg-transparent border-transparent opacity-60"
                              }`}
                          >
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const checked = e.target.checked
                                  setInviteWorkspaces((prev) => ({
                                    ...prev,
                                    [ws.id]: {
                                      enabled: checked,
                                      role: prev[ws.id]?.role || "member",
                                    },
                                  }))
                                }}
                                className="rounded border-zinc-300 text-[#004EB1] focus:ring-[#004EB1] size-3.5 cursor-pointer"
                              />
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-zinc-900 dark:text-white truncate">
                                  {ws.name}
                                </div>
                                <div className="text-[10px] text-zinc-400 truncate">/{ws.slug}</div>
                              </div>
                            </label>

                            <select
                              value={currentWsRole}
                              disabled={!isSelected}
                              onChange={(e) => {
                                const newRole = e.target.value
                                setInviteWorkspaces((prev) => ({
                                  ...prev,
                                  [ws.id]: {
                                    enabled: prev[ws.id]?.enabled ?? true,
                                    role: newRole,
                                  },
                                }))
                              }}
                              className={`rounded border px-2 py-1 text-[11px] bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer ${isSelected
                                ? "border-zinc-200 dark:border-zinc-800"
                                : "border-transparent bg-transparent opacity-40 cursor-not-allowed"
                                }`}
                            >
                              <option value="member">Membro</option>
                              <option value="admin">Administrador</option>
                            </select>
                          </div>
                        )
                      })
                    )}
                  </div>
                  {selectedInviteWsCount === 0 && (
                    <p className="text-[11px] font-semibold text-red-500 dark:text-red-400 flex items-center gap-1">
                      <AlertTriangle className="size-3.5" />
                      É obrigatório selecionar pelo menos 1 workspace para membros regulares.
                    </p>
                  )}
                </div>
              )}

              <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-955/20 border border-amber-200 dark:border-amber-900/30 text-[11px] text-amber-800 dark:text-amber-300 font-sans">
                ⏱️ <strong>Limite de Validade:</strong> O convite terá validade máxima de <strong>72 horas</strong> a partir do envio por e-mail.
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isInviting}
                  onClick={() => setShowInviteModal(false)}
                  className="text-xs h-8 font-sans cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isInviting || (inviteRole !== "admin" && selectedInviteWsCount === 0)}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-sans cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar Convite"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Membro */}
      {editingMember && (() => {
        const isEditingOwner = (editingMember.role || editMemberRole) === "owner"
        const isCurrentUserOwner = profileRole === "owner"

        return (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
              <div className="p-4 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                  <Pencil className="size-4 text-[#004EB1]" />
                  {isEditingOwner ? "Editar Nome do Proprietário" : "Editar Dados e Permissões do Membro"}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Função</label>
                    <select
                      value={editMemberRole}
                      disabled={isEditingOwner}
                      onChange={(e) => setEditMemberRole(e.target.value)}
                      className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isEditingOwner ? (
                        <option value="owner">Proprietário</option>
                      ) : (
                        <>
                          <option value="member">Membro</option>
                          <option value="admin">Administrador</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Status</label>
                    <div className="flex items-center justify-between h-[34px] px-3 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                      <span className={`text-xs font-semibold ${editMemberStatus === "active" ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400 dark:text-zinc-500"}`}>
                        {editMemberStatus === "active" ? "Ativo" : "Desativado"}
                      </span>
                      <button
                        type="button"
                        disabled={isEditingOwner}
                        onClick={() => setEditMemberStatus((prev) => (prev === "active" ? "inactive" : "active"))}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${editMemberStatus === "active" ? "bg-[#004EB1]" : "bg-zinc-200 dark:bg-zinc-800"
                          }`}
                      >
                        <span
                          className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${editMemberStatus === "active" ? "translate-x-4" : "translate-x-0"
                            }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400">Perfil de Acesso Customizado</label>
                  <select
                    value={editMemberProfile}
                    disabled={isEditingOwner}
                    onChange={(e) => setEditMemberProfile(e.target.value)}
                    className="w-full rounded-md border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">{isEditingOwner ? "Acesso Total (Proprietário)" : "Sem perfil atribuído"}</option>
                    {!isEditingOwner && Object.keys(groupedProfiles).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                {/* Seção Workspaces Vinculados (Somente Leitura) */}
                <div className="pt-2 border-t border-zinc-150 dark:border-zinc-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                      <FolderOpen className="size-3.5 text-[#004EB1]" />
                      Workspaces
                    </label>
                  </div>

                  {editMemberRole === "admin" || editMemberRole === "owner" ? (
                    <div className="p-2.5 rounded-lg bg-blue-50/60 dark:bg-blue-955/20 border border-blue-200 dark:border-blue-900/40 text-[11px] text-[#004EB1] dark:text-blue-300 font-medium">
                      🛡️ Como {editMemberRole === "owner" ? "Proprietário" : "Administrador"}, o usuário tem acesso total a todos os workspaces desta empresa.
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/40 space-y-2">
                      {(() => {
                        const memberId = editingMember.userId || editingMember.id
                        const assignedIds = userWorkspacesMap[memberId] || []
                        const assigned = (currentAccount?.workspaces || []).filter((ws) => assignedIds.includes(ws.id))

                        if (assigned.length === 0) {
                          return (
                            <div className="text-[11px] text-zinc-500 italic">
                              Acesso padrão ou nenhum workspace atribuído individualmente.
                            </div>
                          )
                        }

                        return (
                          <div className="flex flex-wrap gap-1.5">
                            {assigned.map((ws) => (
                              <span
                                key={ws.id}
                                className="bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-zinc-200 dark:border-zinc-700 flex items-center gap-1 shadow-2xs"
                              >
                                <FolderOpen className="size-2.5 text-[#004EB1]" />
                                {ws.name}
                              </span>
                            ))}
                          </div>
                        )
                      })()}
                      <p className="text-[10px] text-zinc-400 pt-1">
                        💡 A associação e desassociação de workspaces para este membro pode ser configurada diretamente na aba de Workspaces.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-zinc-150 dark:border-zinc-800 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSavingMember}
                  onClick={() => setEditingMember(null)}
                  className="text-xs h-8 font-sans cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  disabled={isSavingMember || !editMemberName.trim()}
                  onClick={async () => {
                    setIsSavingMember(true)
                    try {
                      const memberId = editingMember.userId || editingMember.id
                      const selectedRoleObj = allCustomRoles.find((r) => r.name === editMemberProfile)
                      const customRoleId = selectedRoleObj ? selectedRoleObj.id : editMemberProfile ? null : null

                      if (handleUpdateAccountMember) {
                        await handleUpdateAccountMember(memberId, {
                          name: editMemberName,
                          role: isEditingOwner ? "owner" : editMemberRole,
                          status: isEditingOwner ? "active" : editMemberStatus,
                          customRoleId: isEditingOwner ? null : customRoleId,
                        })
                      } else if (handleUpdateUserRole) {
                        handleUpdateUserRole(memberId, isEditingOwner ? "owner" : editMemberRole)
                      }

                      toast.success(`Dados do membro ${editMemberName} salvos com sucesso!`)
                      setEditingMember(null)
                    } catch (err: any) {
                      toast.error(err?.message || "Erro ao salvar dados do membro.")
                    } finally {
                      setIsSavingMember(false)
                    }
                  }}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-sans cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSavingMember ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    "Salvar Alterações"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Modal Confirmar Exclusão de Membro */}
      {memberToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto shadow-xs">
              <AlertTriangle className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Excluir Membro da Empresa
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja remover <strong>{memberToDelete.name || memberToDelete.email}</strong>?
                <br />
                O usuário perderá o acesso a esta conta corporativa e a todos os seus workspaces.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isDeletingMember}
                onClick={() => setMemberToDelete(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isDeletingMember || !isAdmin}
                onClick={async () => {
                  if (!isAdmin) {
                    toast.error("Apenas o Proprietário e os Administradores da empresa podem remover membros.")
                    return
                  }
                  setIsDeletingMember(true)
                  try {
                    const memberId = memberToDelete.userId || memberToDelete.id
                    await handleRemoveUser(memberId)
                    toast.success(`Membro ${memberToDelete.name || memberToDelete.email} excluído com sucesso!`)
                    setMemberToDelete(null)
                  } catch (err: any) {
                    toast.error(err?.message || "Erro ao remover membro.")
                  } finally {
                    setIsDeletingMember(false)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-sans cursor-pointer flex-1 flex items-center justify-center gap-1.5"
              >
                {isDeletingMember ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  "Excluir Membro"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Saída da Empresa */}
      {memberToLeave && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto shadow-xs">
              <LogOut className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Sair da Empresa
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja sair de <strong>{currentAccount?.name || "esta empresa"}</strong>?
                <br />
                Você perderá o acesso a esta conta corporativa e a todos os seus workspaces.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isLeavingMember}
                onClick={() => setMemberToLeave(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isLeavingMember}
                onClick={async () => {
                  setIsLeavingMember(true)
                  try {
                    const memberId = memberToLeave.userId || memberToLeave.id
                    await handleRemoveUser(memberId)
                    toast.success(`Você saiu da empresa com sucesso.`)
                    setMemberToLeave(null)
                  } catch (err: any) {
                    toast.error(err?.message || "Erro ao sair da empresa.")
                  } finally {
                    setIsLeavingMember(false)
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 px-4 font-sans cursor-pointer flex-1 flex items-center justify-center gap-1.5"
              >
                {isLeavingMember ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Saindo...
                  </>
                ) : (
                  "Confirmar e Sair"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
